import { Link, useLocation } from "react-router-dom";
import {
  Package, ShoppingCart, List, Menu,
  BadgeDollarSign, MailCheck, ClipboardList,
  PackageOpen, FileCheck, UserPlus, UserCheck,
  StickyNote, ChevronDown, Layers, LayoutDashboard
} from "lucide-react";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import logo from "../images/logo.png";
import { FileImage } from "lucide-react";

const BASE_URL = process.env.REACT_APP_API_URL;


const Sidebar = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useContext(AuthContext);
  const [accessiblePages, setAccessiblePages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Expanded states
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);

  const [orderCounts, setOrderCounts] = useState({ new: 0, packed: 0, confirm: 0 });

  const isActive = (path) => location.pathname === path;

  // Auto-expand parent if child is active
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("order") || path === "/new-orders" || path === "/pending-packed") setOrdersOpen(true);
    if (path.includes("payment")) setPaymentOpen(true);
    if (path.includes("products") || path.includes("courier")) setSetupOpen(true);
    if (path.includes("roles") || path.includes("users")) setAdminOpen(true);
    if (path.includes("heatmap") || path.includes("insights")) setDashboardOpen(true);
  }, [location.pathname]);


  useEffect(() => {
    const fetchAccessiblePages = async () => {
      if (user?.role) {
        try {
          const res = await axios.get(`${BASE_URL}/api/roles/role-name/${user.role}`)
          setAccessiblePages(res.data.accessiblePages || []);
        } catch (err) {
          console.error("Failed to fetch accessible pages", err);
          setAccessiblePages([]);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAccessiblePages();
  }, [user]);

  // Reset counts on visit
  useEffect(() => {
    if (location.pathname === "/web-pending-confirm-orders") {
      setOrderCounts(prev => ({ ...prev, confirm: 0 }));
    }
    if (location.pathname === "/new-orders") {
      setOrderCounts(prev => ({ ...prev, new: 0 }));
    }
    if (location.pathname === "/pending-packed") {
      setOrderCounts(prev => ({ ...prev, packed: 0 }));
    }
  }, [location.pathname]);


  const fetchOrderCounts = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/orders`);
      const newCount = res.data.filter(order => order.status === "New").length;
      const packedCount = res.data.filter(order => order.status === "Pending Packed").length;
      const confirmCount = res.data.filter(order => order.status === "Pending Confirm").length;
      setOrderCounts({ new: newCount, packed: packedCount, confirm: confirmCount });
    } catch (err) {
      console.error("Failed to fetch order counts", err);
    }
  };

  useEffect(() => {
    fetchOrderCounts();
    const interval = setInterval(fetchOrderCounts, 60000); // Polling every minute
    return () => clearInterval(interval);
  }, []);

  const pages = [
    { label: "Summary", path: "/summary", icon: <StickyNote size={20} /> },
    {
      label: "Dashboard",
      icon: <LayoutDashboard size={20} />,
      children: [
        { label: "Order Heatmap", path: "/order-heatmap", icon: <FileImage size={18} /> },
        { label: "Store Insights", path: "/store-insights", icon: <Layers size={18} /> },
        { label: "Profit & Expense", path: "/finance", icon: <BadgeDollarSign size={18} /> },
      ],
    },
    {
      label: "Orders",
      icon: <ClipboardList size={20} />,
      children: [
        { label: "Create Order", path: "/create-order", icon: <ShoppingCart size={18} /> },
        { label: "Pending Confirm", path: "/web-pending-confirm-orders", icon: <Package size={18} /> },
        { label: "New Orders", path: "/new-orders", icon: <ClipboardList size={18} /> },
        { label: "Pending Packed", path: "/pending-packed", icon: <PackageOpen size={18} /> },
        { label: "All Orders", path: "/all-orders", icon: <List size={18} /> },
      ],
    },
    {
      label: "Payment",
      icon: <BadgeDollarSign size={20} />,
      children: [
        { label: "Claim Payment", path: "/payment", icon: <BadgeDollarSign size={18} /> },
        { label: "Payment History", path: "/payment-history", icon: <FileCheck size={18} /> },
      ],
    },
    {
      label: "Setup",
      icon: <Package size={20} />,
      children: [
        { label: "Products", path: "/products", icon: <Package size={18} /> },
        { label: "Add Courier", path: "/add-courier", icon: <MailCheck size={18} /> },
      ],
    },
    {
      label: "Admin Settings",
      icon: <UserPlus size={20} />,
      children: [
        { label: "Add Roles", path: "/add-roles", icon: <UserPlus size={18} /> },
        { label: "Add Users", path: "/add-users", icon: <UserCheck size={18} /> },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="w-64 h-screen flex items-center justify-center border-r bg-white">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={`transition-all duration-300 ${collapsed ? "w-20" : "w-72"} bg-white border-r border-gray-100 h-screen flex flex-col shadow-[1px_0_20px_rgba(0,0,0,0.02)] z-30`}>

      {/* Logo Section */}
      <div className={`h-16 flex items-center ${collapsed ? 'justify-center flex-col gap-2 py-2' : 'justify-between px-4'} border-b border-gray-50 transition-all`}>
        {!collapsed ? (
          <img src={logo} alt="Logo" className="h-8 object-contain" />
        ) : (
          // Collapsed Logo - just a clean Icon
          null
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors ${collapsed ? 'mt-2' : ''}`}
        >
          {collapsed ? <Menu className="h-6 w-6" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="mt-6 flex-1 px-3 space-y-2 overflow-y-auto no-scrollbar pb-6">
        {pages.map((page) => {
          // Permission Check
          if (page.children) {
            const hasPermission = page.children.some((child) => {
              if (child.label === "Store Insights") {
                return accessiblePages.includes("Store Insights") || accessiblePages.includes("Order Heatmap");
              }
              if (child.label === "Profit & Expense") {
                return accessiblePages.includes("Profit & Expense") || accessiblePages.includes("Store Insights");
              }
              return accessiblePages.includes(child.label);
            });
            if (!hasPermission) return null;

            const isOpen = {
              "Orders": ordersOpen,
              "Payment": paymentOpen,
              "Setup": setupOpen,
              "Admin Settings": adminOpen,
              "Dashboard": dashboardOpen,
            }[page.label];

            const setOpen = {
              "Orders": setOrdersOpen,
              "Payment": setPaymentOpen,
              "Setup": setSetupOpen,
              "Admin Settings": setAdminOpen,
              "Dashboard": setDashboardOpen,
            }[page.label];

            const hasActiveChild = page.children.some(child => isActive(child.path));

            // Calculate parent badge count
            let parentBadgeCount = 0;
            if (page.label === "Orders") {
              parentBadgeCount = orderCounts.new + orderCounts.packed + orderCounts.confirm;
            }

            return (
              <div key={page.label} className="mb-1">
                <button
                  onClick={() => {
                    if (collapsed) {
                      setCollapsed(false);
                      setOpen(true);
                    } else {
                      setOpen(!isOpen);
                    }
                  }}
                  className={`group w-full flex items-center ${collapsed ? 'justify-center px-0' : 'justify-between px-3'} py-2.5 rounded-xl text-sm font-medium transition-all duration-200 
                    ${hasActiveChild ? 'bg-blue-50/50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                  `}
                  title={collapsed ? page.label : ""}
                >
                  <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
                    <span className={`transition-colors flex items-center justify-center ${hasActiveChild ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}>
                      {page.icon}
                    </span>
                    {!collapsed && <span className="text-left">{page.label}</span>}
                  </div>

                  {!collapsed && (
                    <div className="flex items-center">
                      {parentBadgeCount > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full mr-2 shadow-sm">
                          {parentBadgeCount > 99 ? '99+' : parentBadgeCount}
                        </span>
                      )}
                      <span className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </div>
                  )}
                  {/* Collapsed Badge Dot */}
                  {collapsed && parentBadgeCount > 0 && (
                    <div className="absolute top-1 right-2 w-2 h-2 bg-red-500 rounded-full ring-1 ring-white"></div>
                  )}
                </button>

                {/* Submenu */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen && !collapsed ? "max-h-96 opacity-100 mt-1" : "max-h-0 opacity-0"
                    }`}
                >
                  <div className="ml-4 pl-4 border-l border-gray-100 space-y-1 py-1">
                    {page.children.map((child) => {
                      // ... (child permission checks same as before)
                      const isStoreInsightsAndAllowed = child.label === "Store Insights" &&
                        (accessiblePages.includes("Store Insights") || accessiblePages.includes("Order Heatmap"));

                      const isProfitAndAllowed = child.label === "Profit & Expense" &&
                        (accessiblePages.includes("Profit & Expense") || accessiblePages.includes("Store Insights"));

                      if (!accessiblePages.includes(child.label) && !isStoreInsightsAndAllowed && !isProfitAndAllowed) return null;

                      const isNew = child.label === "New Orders";
                      const isPending = child.label === "Pending Packed";
                      const isPendingConfirm = child.label === "Pending Confirm";
                      const count = isNew ? orderCounts.new : isPending ? orderCounts.packed : isPendingConfirm ? orderCounts.confirm : 0;

                      const active = isActive(child.path);

                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`flex items-center justify-between px-3 py-2 rounded-lg text-[13px] font-medium transition-all
                             ${active
                              ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                            }
                           `}
                        >
                          <div className="flex items-center gap-2">
                            {child.icon && <span className={active ? 'text-white/80' : 'text-gray-400'}>{child.icon}</span>}
                            <span>{child.label}</span>
                          </div>

                          {count > 0 && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full 
                               ${active ? 'bg-white text-blue-600' : 'bg-blue-100 text-blue-600'}
                             `}>
                              {count}
                            </span>
                          )}
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </div>
            );
          }

          // Direct Link
          if (!accessiblePages.includes(page.label)) return null;
          const active = isActive(page.path);

          return (
            <Link
              key={page.path}
              to={page.path}
              className={`group flex items-center ${collapsed ? 'justify-center' : 'gap-3 px-3'} py-2.5 rounded-xl text-sm font-medium transition-all duration-200 mb-1
                ${active
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
              `}
              title={collapsed ? page.label : ""}
            >
              <span className={`transition-colors ${active ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                {page.icon}
              </span>
              {!collapsed && <span>{page.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-gray-50">
          {/* ... user profile ... */}
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-gray-50">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
              {user?.username?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.username}</p>
              <p className="text-xs text-gray-500 truncate">{user?.role}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
